/**
 * Ask Karyva (2026-10-01): questions across the whole workspace. The backend
 * answers by calling read-only tools (needs attention, forecasts, capacity,
 * products, strategy, intake) — see app/services/workspace_assistant.py.
 * Replies may contain in-app links like [Name](/project/12); they're turned
 * into router links. No HTML is ever rendered from the reply.
 */
import { Fragment, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Send, Sparkles } from 'lucide-react'

import { Button } from '../ui/button'
import { workspaceAssistantService, type AssistantTurn } from '../../services/workspaceAssistant'
import { apiErrorMessage } from '../../utils/apiError'

const SUGGESTIONS = [
  'Which projects are most at risk, and why?',
  'Who is over-committed in the next month?',
  'Which projects will finish late or over budget?',
  'How are our products doing against their targets?',
]

const TOOL_LABEL: Record<string, string> = {
  search: 'searched names', list_projects: 'projects', needs_attention: 'needs attention', project_details: 'project details',
  forecast: 'forecasts', capacity: 'capacity', products: 'products', strategy: 'strategy', intake_queue: 'intake queue',
}

/** Render **bold** and in-app [text](/path) links; everything else is plain text. */
export function RichReply({ text }: { text: string }) {
  const lines = text.split('\n')
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const bullet = /^\s*[-•*]\s+/.test(line)
        const body = line.replace(/^\s*[-•*]\s+/, '')
        const parts = body.split(/(\[[^\]]+\]\(\/[a-z]+(?:\/\d+)?\)|\*\*[^*]+\*\*)/g)
        const content = parts.map((p, j) => {
          const link = p.match(/^\[([^\]]+)\]\((\/[a-z]+(?:\/\d+)?)\)$/)
          if (link) return <Link key={j} to={link[2]} className="text-primary hover:underline">{link[1]}</Link>
          const bold = p.match(/^\*\*([^*]+)\*\*$/)
          if (bold) return <strong key={j}>{bold[1]}</strong>
          return <Fragment key={j}>{p}</Fragment>
        })
        if (!line.trim()) return <div key={i} className="h-1" />
        return bullet
          ? <div key={i} className="flex gap-2 pl-1"><span aria-hidden="true">•</span><span>{content}</span></div>
          : <p key={i}>{content}</p>
      })}
    </div>
  )
}

export function WorkspaceAssistant() {
  const [turns, setTurns] = useState<Array<AssistantTurn & { tools?: string[] }>>([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  const ask = async (q: string) => {
    const question = q.trim()
    if (!question || busy) return
    const history: AssistantTurn[] = [...turns.map(({ role, content }) => ({ role, content })), { role: 'user', content: question }]
    setTurns([...turns, { role: 'user', content: question }])
    setText('')
    setBusy(true)
    setError(null)
    try {
      const res = await workspaceAssistantService.ask(history)
      setTurns((t) => [...t, { role: 'assistant', content: res.reply, tools: res.tools_used }])
    } catch (err) {
      setError(apiErrorMessage(err, 'The assistant couldn’t answer just now. Please try again.'))
    } finally {
      setBusy(false)
      setTimeout(() => endRef.current?.scrollIntoView?.({ block: 'nearest' }), 0)
    }
  }

  return (
    <section aria-label="Ask Karyva" className="mb-8 rounded-xl border border-border bg-card p-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
        <Sparkles className="h-5 w-5 text-primary" /> Ask about your workspace
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Answers come from your live projects, forecasts, capacity, products and strategy — with links to the records.
      </p>

      {turns.length === 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" onClick={() => ask(s)} disabled={busy}
              className="min-h-[36px] rounded-full border border-border bg-background px-3 text-sm text-foreground hover:bg-muted">
              {s}
            </button>
          ))}
        </div>
      )}

      {turns.length > 0 && (
        <div className="mt-4 max-h-[480px] space-y-3 overflow-y-auto pr-1" aria-live="polite">
          {turns.map((t, i) => (
            <div key={i} className={t.role === 'user' ? 'flex justify-end' : ''}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${t.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                {t.role === 'assistant' ? <RichReply text={t.content} /> : t.content}
                {t.tools && t.tools.length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Looked at: {[...new Set(t.tools.map((x) => TOOL_LABEL[x] ?? x))].join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && <p className="text-sm text-muted-foreground">Looking through your workspace…</p>}
          <div ref={endRef} />
        </div>
      )}
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(text) }}>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} aria-label="Your question"
          placeholder="e.g. What changed on the cloud migration and is it still on budget?"
          className="min-h-[44px] flex-1 rounded-md border border-border bg-background px-3 text-sm text-foreground" />
        <Button type="submit" disabled={busy || !text.trim()} className="min-h-[44px] gap-2"><Send className="h-4 w-4" /> Ask</Button>
      </form>
    </section>
  )
}
